import type { ActiveHit, Player } from './types'
import { PLAYER_DISPLAY_HEIGHT } from './balance'

export type CannonTrackingMode = 'legacy' | 'aim' | 'homing' | 'neck'
type Tracking = { mode: Exclude<CannonTrackingMode, 'legacy'>; initialized: boolean; stopped: boolean; direction: number }
type TrackingHit = ActiveHit & { cannonTracking?: Tracking }
export const CANNON_TRACKING = { shotTurnDeg: 45, volleyTurnDeg: 25, stopDistance: 140, fadeDistance: 250, maxDescentDeg: 12, aimHeight: PLAYER_DISPLAY_HEIGHT * (5 / 8) } as const

// 正式採用：通常起動・本番は胴狙い追尾。旧版への切替はDEVだけ。
export function cannonTrackingMode(): CannonTrackingMode {
  if (!import.meta.env.DEV || typeof window === 'undefined') return 'homing'
  const mode = new URLSearchParams(window.location.search).get('cannonTracking')
  return mode === 'legacy' || mode === 'aim' || mode === 'neck' ? mode : 'homing'
}
export function attachCannonTracking(hit: ActiveHit, mode = cannonTrackingMode()): ActiveHit {
  if (!['exec_cannonShot', 'exec_cannonVolley'].includes(hit.attackId)) return hit
  if (mode === 'legacy') {
    const { cannonTracking: _tracking, ...plain } = hit as TrackingHit
    return _tracking ? plain : hit
  }
  return { ...hit, cannonTracking: { mode, initialized: false, stopped: false, direction: Math.sign(hit.vx ?? 1) } } as TrackingHit
}

// 発射時の照準 → 角速度上限つき誘導 → 接近または通過したら永久に直進。
// 反射後は既存のボス胸への直線軌道を保持する。
export function steerCannonProjectile(hit: ActiveHit, player: Player, dtMs: number): ActiveHit {
  const tracking = (hit as TrackingHit).cannonTracking
  if (!tracking || hit.reflected || hit.consumed || hit.x === undefined || hit.y === undefined || hit.vx === undefined) return hit
  const dx = player.x - hit.x
  const previous = tracking.mode === 'neck'
  const aimHeight = previous ? PLAYER_DISPLAY_HEIGHT * (5 / 6) : CANNON_TRACKING.aimHeight
  const dy = player.y - aimHeight - hit.y
  const distance = previous ? Math.hypot(dx, dy) : Math.abs(dx)
  const speed = Math.hypot(hit.vx, hit.vy ?? 0)
  const passed = dx * tracking.direction <= 0
  const stopped = tracking.stopped || passed || distance <= CANNON_TRACKING.stopDistance
  let angle = Math.atan2(hit.vy ?? 0, hit.vx)
  // 砲の前方向を基準に、水平〜下12度に収める。上方・背後へは撃たない。
  const descent = Math.max(0, Math.min(CANNON_TRACKING.maxDescentDeg * Math.PI / 180, Math.atan2(dy, Math.abs(dx))))
  const targetAngle = previous ? Math.atan2(dy, dx) : tracking.direction > 0 ? descent : Math.PI - descent
  if (!tracking.initialized && !passed) angle = targetAngle
  else if ((tracking.mode === 'homing' || previous) && !stopped) {
    const difference = Math.atan2(Math.sin(targetAngle - angle), Math.cos(targetAngle - angle))
    const rate = hit.attackId === 'exec_cannonShot' ? CANNON_TRACKING.shotTurnDeg : CANNON_TRACKING.volleyTurnDeg
    const fade = previous ? 1 : Math.max(0, Math.min(1,
      (distance - CANNON_TRACKING.stopDistance) / (CANNON_TRACKING.fadeDistance - CANNON_TRACKING.stopDistance)))
    const limit = rate * fade * Math.PI / 180 * Math.max(0, dtMs) / 1000
    angle += Math.max(-limit, Math.min(limit, difference))
  }
  return { ...hit, vx: Math.cos(angle) * speed, vy: Math.sin(angle) * speed,
    cannonTracking: { ...tracking, initialized: true, stopped } } as TrackingHit
}

// 比較対象の弾だけ、高さも判定する。横幅は既存判定、縦半径は弾の幅の半分。
export function cannonProjectileOverlapsHeight(hit: ActiveHit, feetY: number, height: number): boolean {
  if (!(hit as TrackingHit).cannonTracking || hit.y === undefined) return true
  const radius = (hit.widthPx ?? 0) / 2
  return hit.y + radius >= feetY - height && hit.y - radius <= feetY
}

export function cannonProjectileDrawAngle(hit: ActiveHit): number {
  return (hit as TrackingHit).cannonTracking ? Math.atan2(hit.vy ?? 0, hit.vx ?? 0) : (hit.vx ?? 0) >= 0 ? 0 : Math.PI
}
